import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
});

// Separate client for /auth/refresh — bypasses interceptors to prevent
// recursion when refresh itself fails.
const refreshClient = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

let refreshPromise = null;

function clearAuthAndRedirect() {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  window.location.href = '/login';
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const orig = error.config;
    const status = error.response?.status;
    // Refresh on 401. Backend uses 401 for expired/invalid access tokens
    // and 403 for permission denials — never refresh on 403.
    if (status === 401 && !orig._retry) {
      orig._retry = true;
      // While impersonating, the active token is a non-refreshable impersonation
      // token. If it 401s (expired/revoked), do NOT refresh — that would mint a
      // token for the wrong identity. Restore the parked admin session instead.
      if (localStorage.getItem('adminAccessToken')) {
        localStorage.setItem('accessToken', localStorage.getItem('adminAccessToken'));
        const adminRefresh = localStorage.getItem('adminRefreshToken');
        if (adminRefresh) localStorage.setItem('refreshToken', adminRefresh);
        localStorage.removeItem('adminAccessToken');
        localStorage.removeItem('adminRefreshToken');
        localStorage.removeItem('impersonator');
        window.location.href = '/users';
        return Promise.reject(error);
      }
      if (!refreshPromise) {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          clearAuthAndRedirect();
          return Promise.reject(error);
        }
        refreshPromise = refreshClient
          .post('/auth/refresh', { refreshToken })
          .then((r) => {
            localStorage.setItem('accessToken', r.data.accessToken);
            localStorage.setItem('refreshToken', r.data.refreshToken);
            return r.data.accessToken;
          })
          .catch(() => {
            clearAuthAndRedirect();
            return null;
          })
          .finally(() => { refreshPromise = null; });
      }
      const newToken = await refreshPromise;
      if (newToken) {
        orig.headers.Authorization = `Bearer ${newToken}`;
        return api(orig);
      }
    }
    return Promise.reject(error);
  }
);

export default api;
